import { ArrowUpDown, Columns, Download, Printer, Search, SlidersHorizontal, X } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Input } from '@/components/ui/input'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import type { TypeSortDirection } from '@/libs/interfaces'

export interface FilterOption {
  key: string
  label: string
  type: 'select' | 'text' | 'date'
  options?: { value: string; label: string }[]
}

export interface SortOption {
  key: string
  label: string
  direction?: TypeSortDirection
}

export interface ColumnConfig {
  key: string
  label: string
  visible: boolean
}

interface SearchBoxProps {
  searchPlaceholder?: string
  searchValue: string
  onSearchChange: (value: string) => void
}

function SearchBox({ searchPlaceholder, searchValue, onSearchChange }: SearchBoxProps) {
  const { t } = useTranslation()

  return (
    <div className="relative flex-1 min-w-[180px] max-w-md">
      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
      <Input
        placeholder={searchPlaceholder || t('common.search')}
        value={searchValue}
        onChange={(e) => onSearchChange(e.target.value)}
        className="pl-9"
      />
      {searchValue && (
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7"
              onClick={() => onSearchChange('')}
              aria-label={t('common.clear')}
            >
              <X className="w-4 h-4" />
            </Button>
          </TooltipTrigger>
          <TooltipContent collisionPadding={16}>{t('tooltips.filters.clearSearch')}</TooltipContent>
        </Tooltip>
      )}
    </div>
  )
}

interface FiltersPopoverProps {
  filters: FilterOption[]
  activeFilters: Record<string, string>
  onFilterChange?: (key: string, value: string) => void
  onClearFilters?: () => void
  open: boolean
  onOpenChange: (open: boolean) => void
  activeFilterCount: number
}

function FiltersPopover({
  filters,
  activeFilters,
  onFilterChange,
  onClearFilters,
  open,
  onOpenChange,
  activeFilterCount,
}: FiltersPopoverProps) {
  const { t } = useTranslation()

  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <Tooltip>
        <PopoverTrigger asChild>
          <TooltipTrigger asChild>
            <Button
              variant="outline"
              size="sm"
              className="relative"
              aria-label={t('common.filter')}
            >
              <SlidersHorizontal className="w-4 h-4 lg:mr-2" />
              <span className="hidden lg:inline">{t('common.filter')}</span>
              {activeFilterCount > 0 && (
                <span className="ml-2 bg-primary text-primary-foreground text-xs px-1.5 py-0.5 rounded-full">
                  {activeFilterCount}
                </span>
              )}
            </Button>
          </TooltipTrigger>
        </PopoverTrigger>
        <TooltipContent collisionPadding={16}>{t('tooltips.filters.filter')}</TooltipContent>
      </Tooltip>
      <PopoverContent
        className="w-[min(20rem,calc(100vw-2rem))]"
        align="start"
        sideOffset={4}
        collisionPadding={16}
      >
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h4 className="font-medium">{t('filters.title')}</h4>
            {activeFilterCount > 0 && onClearFilters && (
              <Button variant="ghost" size="sm" onClick={onClearFilters}>
                {t('filters.clearAll')}
              </Button>
            )}
          </div>
          {filters.map((filter) => (
            <div key={filter.key} className="space-y-2">
              <label className="text-sm font-medium">{filter.label}</label>
              {filter.type === 'select' && filter.options && (
                <Select
                  value={activeFilters[filter.key] || 'all'}
                  onValueChange={(value) => onFilterChange?.(filter.key, value)}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{t('common.all')}</SelectItem>
                    {filter.options.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
              {filter.type === 'text' && (
                <Input
                  value={activeFilters[filter.key] || ''}
                  onChange={(e) => onFilterChange?.(filter.key, e.target.value)}
                  placeholder={filter.label}
                />
              )}
            </div>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  )
}

interface SortDropdownProps {
  sortOptions: SortOption[]
  currentSort: string | undefined
  sortDirection: TypeSortDirection
  onSortChange?: (key: string, direction: TypeSortDirection) => void
}

function SortDropdown({
  sortOptions,
  currentSort,
  sortDirection,
  onSortChange,
}: SortDropdownProps) {
  const { t } = useTranslation()

  return (
    <DropdownMenu>
      <Tooltip>
        <DropdownMenuTrigger asChild>
          <TooltipTrigger asChild>
            <Button variant="outline" size="sm" aria-label={t('filters.sort')}>
              <ArrowUpDown className="w-4 h-4 lg:mr-2" />
              <span className="hidden lg:inline">{t('filters.sort')}</span>
            </Button>
          </TooltipTrigger>
        </DropdownMenuTrigger>
        <TooltipContent collisionPadding={16}>{t('tooltips.filters.sort')}</TooltipContent>
      </Tooltip>
      <DropdownMenuContent align="end">
        <DropdownMenuLabel>{t('filters.sortBy')}</DropdownMenuLabel>
        <DropdownMenuSeparator />
        {sortOptions.map((opt) => (
          <DropdownMenuCheckboxItem
            key={opt.key}
            checked={currentSort === opt.key}
            onCheckedChange={() =>
              onSortChange?.(opt.key, sortDirection === 'asc' ? 'desc' : 'asc')
            }
          >
            {opt.label}
            {currentSort === opt.key && (
              <span className="ml-auto text-xs text-muted-foreground">
                {sortDirection === 'asc' ? '↑' : '↓'}
              </span>
            )}
          </DropdownMenuCheckboxItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

interface ColumnToggleDropdownProps {
  columns: ColumnConfig[]
  onColumnToggle?: (key: string) => void
}

function ColumnToggleDropdown({ columns, onColumnToggle }: ColumnToggleDropdownProps) {
  const { t } = useTranslation()

  return (
    <DropdownMenu>
      <Tooltip>
        <DropdownMenuTrigger asChild>
          <TooltipTrigger asChild>
            <Button variant="outline" size="sm" aria-label={t('filters.columns')}>
              <Columns className="w-4 h-4 lg:mr-2" />
              <span className="hidden lg:inline">{t('filters.columns')}</span>
            </Button>
          </TooltipTrigger>
        </DropdownMenuTrigger>
        <TooltipContent collisionPadding={16}>{t('tooltips.filters.columns')}</TooltipContent>
      </Tooltip>
      <DropdownMenuContent align="end">
        <DropdownMenuLabel>{t('filters.toggleColumns')}</DropdownMenuLabel>
        <DropdownMenuSeparator />
        {columns.map((col) => (
          <DropdownMenuCheckboxItem
            key={col.key}
            checked={col.visible}
            onCheckedChange={() => onColumnToggle?.(col.key)}
          >
            {col.label}
          </DropdownMenuCheckboxItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

interface ExportDropdownProps {
  onExport: (format: 'csv' | 'xlsx' | 'pdf') => void
}

function ExportDropdown({ onExport }: ExportDropdownProps) {
  const { t } = useTranslation()

  return (
    <DropdownMenu>
      <Tooltip>
        <DropdownMenuTrigger asChild>
          <TooltipTrigger asChild>
            <Button variant="outline" size="sm" aria-label={t('filters.export')}>
              <Download className="w-4 h-4 lg:mr-2" />
              <span className="hidden lg:inline">{t('filters.export')}</span>
            </Button>
          </TooltipTrigger>
        </DropdownMenuTrigger>
        <TooltipContent collisionPadding={16}>{t('tooltips.filters.export')}</TooltipContent>
      </Tooltip>
      <DropdownMenuContent align="end">
        <DropdownMenuCheckboxItem onCheckedChange={() => onExport('csv')}>
          {t('filters.exportCSV')}
        </DropdownMenuCheckboxItem>
        <DropdownMenuCheckboxItem onCheckedChange={() => onExport('xlsx')}>
          {t('filters.exportExcel')}
        </DropdownMenuCheckboxItem>
        <DropdownMenuCheckboxItem onCheckedChange={() => onExport('pdf')}>
          {t('filters.exportPDF')}
        </DropdownMenuCheckboxItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

interface PrintButtonProps {
  onPrint: () => void
}

function PrintButton({ onPrint }: PrintButtonProps) {
  const { t } = useTranslation()

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button variant="outline" size="sm" onClick={onPrint} aria-label={t('filters.print')}>
          <Printer className="w-4 h-4 lg:mr-2" />
          <span className="hidden lg:inline">{t('filters.print')}</span>
        </Button>
      </TooltipTrigger>
      <TooltipContent collisionPadding={16}>{t('tooltips.filters.print')}</TooltipContent>
    </Tooltip>
  )
}

interface ListFiltersProps {
  searchPlaceholder?: string
  searchValue: string
  onSearchChange: (value: string) => void
  filters?: FilterOption[]
  activeFilters?: Record<string, string>
  onFilterChange?: (key: string, value: string) => void
  onClearFilters?: () => void
  sortOptions?: SortOption[]
  currentSort?: string
  sortDirection?: TypeSortDirection
  onSortChange?: (key: string, direction: TypeSortDirection) => void
  columns?: ColumnConfig[]
  onColumnToggle?: (key: string) => void
  onExport?: (format: 'csv' | 'xlsx' | 'pdf') => void
  onPrint?: () => void
  showExport?: boolean
  showPrint?: boolean
  showColumnToggle?: boolean
  hideSearch?: boolean
}

interface ListFiltersDefaults {
  filters: FilterOption[]
  activeFilters: Record<string, string>
  sortOptions: SortOption[]
  sortDirection: TypeSortDirection
  columns: ColumnConfig[]
  showExport: boolean
  showPrint: boolean
  showColumnToggle: boolean
  hideSearch: boolean
}

const LIST_FILTERS_DEFAULTS: ListFiltersDefaults = {
  filters: [],
  activeFilters: {},
  sortOptions: [],
  sortDirection: 'asc',
  columns: [],
  showExport: true,
  showPrint: true,
  showColumnToggle: true,
  hideSearch: false,
}

const resolveCurrentSort = (currentSort: string | undefined, sortOptions: SortOption[]) =>
  currentSort ?? sortOptions[0]?.key

export function ListFilters(rawProps: ListFiltersProps) {
  const { searchPlaceholder, searchValue, onSearchChange, onFilterChange, onClearFilters } =
    rawProps
  const { onSortChange, onColumnToggle, onExport, onPrint } = rawProps
  // Nullish-coalesce each field individually rather than spreading rawProps over
  // LIST_FILTERS_DEFAULTS: an intermediate wrapper (e.g. ListingToolbar) may forward an
  // optional prop it never received as an explicit `undefined`, which a spread would still
  // copy over the default since the key is present on the object.
  const filters = rawProps.filters ?? LIST_FILTERS_DEFAULTS.filters
  const activeFilters = rawProps.activeFilters ?? LIST_FILTERS_DEFAULTS.activeFilters
  const sortOptions = rawProps.sortOptions ?? LIST_FILTERS_DEFAULTS.sortOptions
  const sortDirection = rawProps.sortDirection ?? LIST_FILTERS_DEFAULTS.sortDirection
  const columns = rawProps.columns ?? LIST_FILTERS_DEFAULTS.columns
  const showExport = rawProps.showExport ?? LIST_FILTERS_DEFAULTS.showExport
  const showPrint = rawProps.showPrint ?? LIST_FILTERS_DEFAULTS.showPrint
  const showColumnToggle = rawProps.showColumnToggle ?? LIST_FILTERS_DEFAULTS.showColumnToggle
  const hideSearch = rawProps.hideSearch ?? LIST_FILTERS_DEFAULTS.hideSearch
  const currentSort = resolveCurrentSort(rawProps.currentSort, sortOptions)

  const [filtersOpen, setFiltersOpen] = useState(false)

  const activeFilterCount = Object.values(activeFilters).filter((v) => v && v !== 'all').length

  return (
    <div className="flex flex-wrap items-center gap-2">
      {/* Search */}
      {!hideSearch && (
        <SearchBox
          searchPlaceholder={searchPlaceholder}
          searchValue={searchValue}
          onSearchChange={onSearchChange}
        />
      )}

      <div className="flex items-center gap-2 flex-wrap">
        {/* Filters */}
        {filters.length > 0 && (
          <FiltersPopover
            filters={filters}
            activeFilters={activeFilters}
            onFilterChange={onFilterChange}
            onClearFilters={onClearFilters}
            open={filtersOpen}
            onOpenChange={setFiltersOpen}
            activeFilterCount={activeFilterCount}
          />
        )}

        {/* Sort */}
        {sortOptions.length > 0 && (
          <SortDropdown
            sortOptions={sortOptions}
            currentSort={currentSort}
            sortDirection={sortDirection}
            onSortChange={onSortChange}
          />
        )}

        {/* Column Toggle */}
        {showColumnToggle && columns.length > 0 && (
          <ColumnToggleDropdown columns={columns} onColumnToggle={onColumnToggle} />
        )}

        {/* Export */}
        {showExport && onExport && <ExportDropdown onExport={onExport} />}

        {/* Print */}
        {showPrint && onPrint && <PrintButton onPrint={onPrint} />}
      </div>
    </div>
  )
}
